import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  WritableSignal,
  input,
  output,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { DuelTierDataWithMeta } from '../../../../core/services/tiers/tiers.service';
import { FreeMatchTierData } from '../../../../core/api/player/schemas/tiers';
import { SoloTierData } from '../../../../core/api/player/schemas/solo-tiers';
import {
  activeFreeMatches,
  mergeFreeMatchesIntoTiers,
  sameFreeMatches,
  trackTierListItem,
} from '../../../../core/services/tiers/free-match.utils';
import { TournamentDataModel } from '../../../../core/services/tournament/models/tournament-data-model';
import { WaitingOpponentsData } from '../../../../core/api/player/schemas/waiting-opponents';
import { ThemeService } from '../../../../core/services/theme/theme.service';
import { OperatorService } from '../../../../core/services/operator/operator.service';
import { ThemePipe } from '../../../../core/pipes/theme.pipe';
import { TournamentInfoCardComponent } from '../../../tournaments/components/tournament-info-card/tournament-info-card.component';
import { PracticeCardComponent } from '../practice-card/practice-card.component';
import { WagerCardComponent } from '../wager-card/wager-card.component';
import { FreeMatchCardComponent } from '../free-match-card/free-match-card.component';
import { SoloCardComponent } from '../../../solo/components/solo-card/solo-card.component';
import { IconComponent } from '../../../../ui/icon/icon/icon.component';
import { environment } from '../../../../../environments/environment';
import { OperatorId } from '../../../../core/services/operator/models/operator-id';
import {
  DuelPaymentEventData,
  FreeMatchPlayEventData,
  TournamentPaymentEventData,
} from 'src/app/core/services/tiers/models/payment-events';

@Component({
  selector: 'app-wager-tier-list',
  templateUrl: './wager-tier-list.component.html',
  styleUrls: ['./wager-tier-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgStyle,
    TranslocoDirective,
    TournamentInfoCardComponent,
    PracticeCardComponent,
    NgTemplateOutlet,
    WagerCardComponent,
    FreeMatchCardComponent,
    SoloCardComponent,
    IconComponent,
    ThemePipe,
  ],
})
export class WagerTierListComponent implements OnInit, OnDestroy {
  readonly isInDrawer = input(false);
  readonly allTiers = input.required<Signal<DuelTierDataWithMeta[]>>();
  readonly freeMatches = input.required<Signal<FreeMatchTierData[]>>();
  readonly currentTournamentData = input.required<Signal<TournamentDataModel | undefined>>();
  // Empty while Solo is off for this operator, game or player, which hides the Solo card.
  readonly soloTiers = input.required<Signal<SoloTierData[]>>();
  // Disables the Solo card while any payment is in flight, as the tier tiles disable themselves.
  readonly paymentInFlight = input.required<Signal<boolean>>();
  readonly waitingOpponentsStandard = input.required<Signal<WaitingOpponentsData | undefined>>();

  readonly wagerClickEvent = output<DuelPaymentEventData>();
  readonly tournamentPlayClickEvent = output<TournamentPaymentEventData>();
  readonly freeMatchClickEvent = output<FreeMatchPlayEventData>();
  // Carries no tier: the stake is chosen on the Solo sheet this opens.
  readonly soloClickEvent = output<void>();
  readonly practiceClickEvent = output<void>();
  readonly localClickEvent = output<void>();

  private readonly currentTime: WritableSignal<Date> = signal(new Date());
  private tickIntervalId: ReturnType<typeof setInterval> | null = null;

  protected readonly operatorService = inject(OperatorService);
  protected readonly themeService = inject(ThemeService);

  readonly unlockCelebrationActive = signal(false);

  readonly titleStyle: [string, string][] = [
    ['type', 'panel-wager-tier-list-title'],
    ['text-color', 'panel-wager-tier-list-title'],
  ];

  readonly bodyStyle: [string, string][] = [
    ['type', 'panel-wager-tier-list-body'],
    ['text-color', 'panel-wager-tier-list-body'],
  ];

  readonly playerWaitingStyle: [string, string][] = [
    ['type', 'panel-wager-tier-list-footer'],
    ['text-color', 'panel-wager-tier-list-footer'],
  ];

  readonly tick = this.currentTime.asReadonly();
  // Expiry is decided on the shared 1 s tick, so a lapsed grant vanishes without its own timer.
  // The equality check stops the tick propagating while the visible set is unchanged: the
  // grid below only rebuilds when a grant appears, lapses, or is refetched.
  readonly visibleFreeMatches = computed(
    () => activeFreeMatches(this.freeMatches()(), this.tick().getTime()),
    { equal: sameFreeMatches },
  );
  // A grant on a paid tier's id takes that tier's cell; because this derives from the tick
  // through visibleFreeMatches, the paid card comes back the second the grant lapses.
  readonly tierListItems = computed(() =>
    mergeFreeMatchesIntoTiers(this.allTiers()(), this.visibleFreeMatches()),
  );
  protected readonly trackTierListItem = trackTierListItem;
  readonly showTournamentCard = computed(() => {
    const tournamentData = this.currentTournamentData()();
    return tournamentData !== undefined && tournamentData.status !== 'FINISHED';
  });
  // The full-width practice row always comes after the tiers, so it overrides practiceFirst.
  readonly practiceRow = computed(() => this.themeService.wagerDrawerConfig().practiceRow);
  readonly isPracticeFirst = computed(
    () => this.themeService.wagerDrawerConfig().practiceFirst && !this.practiceRow(),
  );
  readonly hidePractice = computed(() => this.themeService.wagerDrawerConfig().hidePractice);
  readonly canShowLocal =
    environment.target !== 'production' &&
    (this.operatorService.operatorId === OperatorId.thndr ||
      this.operatorService.operatorId === OperatorId.fakebet);

  ngOnInit(): void {
    this.tickIntervalId = setInterval(() => {
      this.currentTime.set(new Date());
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.tickIntervalId !== null) {
      clearInterval(this.tickIntervalId);
      this.tickIntervalId = null;
    }
  }

  onPracticeClick(): void {
    this.practiceClickEvent.emit();
  }

  onLocalClick(): void {
    this.localClickEvent.emit();
  }

  onWagerClick(data: DuelPaymentEventData): void {
    this.wagerClickEvent.emit(data);
  }

  onFreeMatchClick(data: FreeMatchPlayEventData): void {
    this.freeMatchClickEvent.emit(data);
  }

  onSoloClick(): void {
    this.soloClickEvent.emit();
  }

  onTournamentPlayClick(data: TournamentPaymentEventData): void {
    this.tournamentPlayClickEvent.emit(data);
  }

  onUnlockCelebrationComplete(): void {
    this.unlockCelebrationActive.set(true);
    setTimeout(() => this.unlockCelebrationActive.set(false), 0);
  }
}
